import { useEffect, useState, useContext } from 'react'
import Sidebar from '../components/Sidebar'
import { DataContext } from '../context/dataContext';
import { getAllImages } from '../utils/api';
import { Button, CircularProgress } from '@mui/material';
import JSZip from 'jszip';
import { saveAs } from "file-saver";
import AdvanceDropDown from '../components/AdvanceDropDown';
import DateRangePicker from '../components/DateRangePicker';
import { errorMessage } from '../components/Toaster';
import AdvancedSubGropuDropDown from '../components/AdvancedSubGroupDropdown';

function ScanFiles() {
    const { machines, crops } = useContext(DataContext);
    const [selectedCrop, setSelectedCrop] = useState(null)
    const [selectedMachine, setSelectedMachine] = useState(null)
    const [loading, setLoading] = useState(false)
    const [uniqueCropList, setUniqueCropList] = useState([])
    const [varietyList, setVarietyList] = useState([])
    const [dateRange, setDateRange] = useState('')
    const [selectedVeriety, setSelectedVeriety] = useState([])
    const [customDateRange, setCustomDateRange] = useState({ startDate: '', endDate: '' })
    
    useEffect(() => {
        const uniqueObjects = crops?.length && crops.reduce((unique, currentObj) => {
            const isDuplicate = unique.some(obj => obj.crop_name === currentObj.crop_name)
            if (!isDuplicate) { unique.push(currentObj) }
            return unique;
        }, []);
        setUniqueCropList(uniqueObjects);
    }, [crops])

    useEffect(() => {
        if (selectedCrop?.length) {
            const all_verites_crops = crops?.length && crops.filter(item =>
                selectedCrop.some(selectedItem => selectedItem.crop_name === item.crop_name)
            );

            const perfectCrops = all_verites_crops.filter(item =>
                item?.variety_name?.toLowerCase().includes('perfect')
            );

            const filteredCrops = all_verites_crops.filter(item =>
                !item?.variety_name?.toLowerCase().includes('perfect')
            );

            const output_list = filteredCrops.reduce((accumulator, crop) => {
                const existingCrop = accumulator.find((item) => item.title.toLowerCase() === crop.crop_name.toLowerCase());
                if (existingCrop) {
                    existingCrop.data.push(crop);
                } else {
                    accumulator.push({
                        title: crop.crop_name,
                        data: [crop],
                    });
                }
                return accumulator;
            }, []);

            if (perfectCrops.length) {
                output_list.push({
                    title: 'Custom Variety',
                    data: perfectCrops,
                });
            }

            setVarietyList(output_list);
        } else {
            setVarietyList([]);
        }
    }, [selectedCrop, crops]);

    useEffect(() => {
        if (customDateRange && customDateRange.length === 2) {
            const startDate = customDateRange[0].toDate();
            const endDate = customDateRange[1].toDate();
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(23, 59, 59, 999);

            setDateRange({ startDate, endDate });

            // Validate date range
            const daysDifference = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
            
            if (daysDifference > 10) {
                alert(`Date range is ${daysDifference} days. Please select a range of maximum 10 days.`);
            } 
        } else {
            setDateRange('');
        }
    }, [customDateRange]);

    // Check if button should be disabled
    const isButtonDisabled = () => {
        return !(
            selectedCrop?.length &&
            selectedVeriety?.length &&
            selectedMachine?.length &&
            dateRange &&
            dateRange.startDate &&
            dateRange.endDate
        );
    };

    const fetchImages = async () => {
        // Final validation before fetching
        if (!dateRange || !dateRange.startDate || !dateRange.endDate) {
            errorMessage('Please select a date range');
            return;
        }

        const daysDifference = Math.ceil(
            (dateRange.endDate - dateRange.startDate) / (1000 * 60 * 60 * 24)
        );

        if (daysDifference > 10) {
            errorMessage('Date range cannot exceed 10 days');
            return;
        }

        try {
            setLoading(true);
            const combinations = selectedVeriety.flatMap((variety) =>
                selectedMachine.map((machine) => ({
                    crop_id: variety.crop_id,
                    variety_id: variety.variety_id,
                    machine_id: machine.machine_id,
                }))
            );

            const payload = {
                crop_variety_machine: combinations,
                start_date: dateRange.startDate,
                end_date: dateRange.endDate,
            };

            const response = await getAllImages(payload);
            console.log(response?.data?.data);

            await downloadZip(response?.data?.data);

        } catch (error) {
            console.error(error);
            errorMessage('Something went wrong');
        } finally {
            setLoading(false);
        }
    };

    const downloadZip = async (scans_list) => {
        if (!scans_list?.length) {
            errorMessage('No images found for the selected criteria');
            return;
        }

        const zip = new JSZip();

        for (const scan of scans_list) {
            const {
                scan_id,
                sub_scan_id,
                created_at,
                scan_files
            } = scan;

            if (!scan_files?.length) continue;

            const scanFolder = zip.folder(scan_id);

            const safeDate = created_at
                .replace(/[:,]/g, "")
                .replace(/\s+/g, "_");

            for (const file of scan_files) {
                try {
                    const response = await fetch(file.file_url);
                    const blob = await response.blob();

                    const fileName = `${scan_id}_${sub_scan_id}_${safeDate}_${file.file_name}`;

                    scanFolder.file(fileName, blob);
                } catch (err) {
                    console.error("Failed to download:", file.file_url);
                }
            }
        }

        const zipBlob = await zip.generateAsync({ type: "blob" });
        saveAs(zipBlob, "scan_images.zip");
    };

    return (
        <div>
            <div className="scan-content-container">
                <Sidebar />
                <div className="scan-container" style={{ marginTop: 100 }}>
                    <div className="input-group">
                        <AdvanceDropDown
                            title="Crop"
                            width={200}
                            data_list={uniqueCropList}
                            unique_key={'crop_id'}
                            show_options_item={(option) => `${option.crop_name}`}
                            show_name_on_drop_down_box={(option) => `${option.crop_name}`}
                            filter_items={['crop_name']}
                            get_selected_data={setSelectedCrop}
                            required={true}
                        />
                        <div style={{ marginLeft: 10 }}>
                            <AdvancedSubGropuDropDown
                                title='Variety'
                                data_list={varietyList}
                                unique_key={'variety_id'}
                                show_options_item={(option) => `${option.variety_name}`}
                                show_name_on_drop_down_box={(option) => `${option.variety_name}`}
                                filter_items={['variety_name']}
                                width={200}
                                get_selected_data={setSelectedVeriety}
                                required
                            />
                        </div>
                        <div style={{ marginLeft: 10 }}>
                            <AdvanceDropDown
                                title="Machine"
                                width={200}
                                data_list={machines}
                                unique_key={'machine_code'}
                                show_options_item={(option) => `${option.machine_name} - ${option.machine_code}`}
                                show_name_on_drop_down_box={(option) => `${option.machine_name} - ${option.machine_code}`}
                                filter_items={['machine_name', 'machine_code']}
                                get_selected_data={setSelectedMachine}
                                required={true}
                            />
                        </div>
                        <div style={{ marginLeft: 10 }}>
                            <DateRangePicker setDateRange={setCustomDateRange} />
                        </div>
                        <div style={{ marginLeft: 30 }}>
                            <Button 
                                style={{ 
                                    backgroundColor: isButtonDisabled() ? 'lightgray' : 'green', 
                                    color: 'white', 
                                    height: 55,
                                    cursor: isButtonDisabled() ? 'not-allowed' : 'pointer'
                                }} 
                                disabled={isButtonDisabled()} 
                                onClick={fetchImages}
                            >
                                Fetch Images
                            </Button>
                        </div>
                    </div>

                    {/* Loading and Error Messages */}
                    <div style={{ marginTop: 20 }}>
                        {loading && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <CircularProgress size={24} />
                                <span>Downloading images...</span>
                            </div>
                        )}
                    </div>

                </div>
            </div>
        </div>
    )
}

export default ScanFiles